/**
 * The exam clock.
 *
 * The deadline belongs to the server. A device clock can be wrong by minutes or
 * set deliberately, so it is never read as an authority: every response carries
 * the server's time, the difference between the two is measured once, and the
 * time remaining is worked out from the deadline through that difference.
 *
 * Time remaining is always recomputed from the deadline rather than counted
 * down, so a tab that a phone suspended in the background comes back showing
 * the truth instead of the minutes it slept through.
 */

/** How far the device clock sits from the server's, in milliseconds. */
export const clockOffsetMs = (serverNow: string | Date, deviceNowMs: number = Date.now()): number => {
  const server = new Date(serverNow).getTime();

  if (!Number.isFinite(server)) {
    // Nothing usable came back, so trust the device rather than break the clock.
    return 0;
  }

  return server - deviceNowMs;
};

/** Seconds left on a paper, never negative. */
export const remainingSecondsAt = (
  deadlineAt: string | Date,
  offsetMs = 0,
  deviceNowMs: number = Date.now(),
): number => {
  const deadline = new Date(deadlineAt).getTime();

  if (!Number.isFinite(deadline)) {
    return 0;
  }

  return Math.max(0, Math.ceil((deadline - (deviceNowMs + offsetMs)) / 1000));
};

export const hasExpired = (
  deadlineAt: string | Date,
  offsetMs = 0,
  deviceNowMs: number = Date.now(),
): boolean => remainingSecondsAt(deadlineAt, offsetMs, deviceNowMs) <= 0;

/**
 * The warnings still to come, given how much time is left.
 *
 * A warning fires once, as the clock passes it. Crossing several at once, which
 * happens when a suspended tab wakes, should show only the most urgent rather
 * than a queue of stale ones.
 */
export const warningsCrossed = (
  warnAtMinutes: number[],
  previousSeconds: number,
  currentSeconds: number,
): number[] =>
  (warnAtMinutes ?? [])
    .filter((minute) => {
      const threshold = minute * 60;
      return previousSeconds > threshold && currentSeconds <= threshold;
    })
    .sort((a, b) => a - b);

/** The one warning worth showing when several were crossed together. */
export const mostUrgentWarning = (crossed: number[]): number | undefined => crossed[0];
